/**
 * TISA BIZBOOK - Student Profile Page
 * Your journey. Your hustle. Your legend.
 * Facebook meets LinkedIn meets Hogwarts ledger for kid entrepreneurs
 */

import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import type { TISAUser } from "@/types/tisa-coins";
import type { Json } from "@/types/supabase";
import {
  computeStudentStats,
  buildTimeline,
  getShowcaseBadges,
  getLegendStats,
} from "@/lib/tisa-helpers";
import { getGuildById } from "@/config/tisa-settings";
import { LegendHeroCard } from "./components/LegendHeroCard";
import { StatsOverviewSection } from "./components/StatsOverviewSection";
import { TimelineSection } from "./components/TimelineSection";
import { BadgeShowcaseSection } from "./components/BadgeShowcaseSection";
import { ProjectsSection } from "./components/ProjectsSection";
import { initializeLegendData } from "@/lib/tisa-helpers";

export default function BizBookPage() {
  const { user: authUser } = useAuth();
  const [user, setUser] = useState<TISAUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authUser) {
      setError("Please log in to view your BizBook");
      setLoading(false);
      return;
    }

    loadUserData();
  }, [authUser]);

  const loadUserData = async () => {
    if (!authUser) return;

    try {
      setLoading(true);
      // Students are in children table
      const { data, error: fetchError } = await supabase
        .from("children")
        .select("*")
        .eq("id", authUser.id)
        .single();

      if (fetchError) throw fetchError;

      // Initialize legend_data if it doesn't exist
      let userData = data as unknown as TISAUser;
      if (!userData.legend_data) {
        const defaultLegendData = initializeLegendData();

        // Update database with default structure (students in children table)
        const { error: updateError } = await supabase
          .from("children")
          .update({ legend_data: defaultLegendData as unknown as Json })
          .eq("id", authUser.id);

        if (updateError)
          console.error("Failed to initialize legend_data:", updateError);

        userData = { ...userData, legend_data: defaultLegendData };
      }

      setUser(userData);
      setError(null);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load profile data";
      console.error("Error loading user data:", err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple mx-auto mb-4"></div>
          <p className="text-white text-xl font-cinzel">
            Loading your BizBook...
          </p>
        </div>
      </div>
    );
  }

  // Error State
  if (error || !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-red-900 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 text-2xl font-cinzel mb-4">❌ Error</p>
          <p className="text-gray-300">{error || "User not found"}</p>
          <button
            onClick={loadUserData}
            className="mt-4 px-6 py-2 bg-purple hover:bg-purple-600 text-white rounded-lg font-semibold transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Compute all data
  const stats = computeStudentStats(user);
  const guild = user.guild_id ? (getGuildById(user.guild_id) ?? null) : null;
  const timeline = buildTimeline(user);
  const showcaseBadges = getShowcaseBadges(user);
  const legendStats = getLegendStats(user);
  const projects = user.legend_data?.showcase_projects || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      <main className="container mx-auto px-4 py-8">
        {/* Header Card */}
        <LegendHeroCard
          user={user}
          guild={guild}
          currentLevel={stats.current_level}
          totalXP={stats.total_xp}
          onRefresh={loadUserData}
        />

        {/* 3-Column Grid Layout */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column: Stats + Timeline */}
          <div className="lg:col-span-2 space-y-6">
            {/* Timeline */}
            <TimelineSection timeline={timeline} />

            {/* Projects */}
            <ProjectsSection
              userId={user.id}
              projects={projects}
              onRefresh={loadUserData}
            />
          </div>

          {/* Right Column: Stats + Badges */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <StatsOverviewSection
              totalXP={legendStats.totalXP}
              badgesEarned={legendStats.badgesEarned}
              questsCompleted={legendStats.questsCompleted}
              totalWealth={legendStats.totalWealth}
              achievementsUnlocked={legendStats.achievementsUnlocked}
              timelineEvents={legendStats.timelineEvents}
            />

            {/* Badge Showcase */}
            <BadgeShowcaseSection showcaseBadges={showcaseBadges} />
          </div>
        </div>
      </main>
    </div>
  );
}
